import { Add } from "assets/icons/Common";
import { ReactComponent as PlayEdLogoSVG } from "assets/playEdLogo.svg";
import { Button, Col } from "components/primitives";
import { DashboardContext, INITIAL_REMEMBER_SETTINGS } from "contexts/DashboardContext";
import { useContext, useEffect } from "react";
import { Container } from "react-bootstrap";
import styled from "styled-components";
import { DashboardProps } from "types/GlobalData";
import { sanitizeDashboardGroups } from "util/dashboardGroups";
import { saveData } from "util/wp-api";
import { v4 as uuidv4 } from "uuid";

import { GroupItem } from "./group/GroupItem";
import { getRandomBackground } from "./multipleDashboard/utils";

interface Props {}

export const Workspace: React.FC<Props> = () => {
  const { data: globalData, setData: updateGlobalData } = useContext(DashboardContext);

  // Persisted groups can contain null entries or null dashboards, so the rendered
  // list is sanitized once here and reused for every derived value below.
  const dashboardGroups = sanitizeDashboardGroups(globalData.dashboardGroups);
  console.log("dashboardGroups--->:", dashboardGroups);
  const allDashboards = dashboardGroups.reduce(
    (allDashboards, group) => allDashboards.concat(group.dashboards),
    [] as DashboardProps[],
  );

  // Ensure active dashboard state is properly set when component mounts
  useEffect(() => {
    if (dashboardGroups.length > 0 && !globalData.activeDashboardGroupId) {
      // If no active dashboard group is set but groups exist, set the first one as active
      const firstGroup = dashboardGroups[0];
      updateGlobalData({
        activeDashboardGroupId: firstGroup.id,
      });
    }
  }, [dashboardGroups, globalData.activeDashboardGroupId, updateGlobalData]);

  const handleCreateNewGroup = async () => {
    const newDashboard: DashboardProps = {
      id: uuidv4(),
      name: "New Slide",
      backgroundName: getRandomBackground(allDashboards),
      fontName: "LEMON MILK Pro, Arial, sans-serif",
      rememberSettings: INITIAL_REMEMBER_SETTINGS,
      voiceType: "Female",
    };
    const newGroupId = uuidv4();
    const newGroup = {
      id: newGroupId,
      name: "New Group",
      dashboards: [newDashboard],
    };

    const newGlobalData = {
      dashboardGroups: [...(globalData?.dashboardGroups || []), newGroup],
      activeDashboardGroupId: newGroupId,
      activeDashboardId: newDashboard.id,
    };

    updateGlobalData(newGlobalData);

    // Ensure the state is immediately saved to WordPress
    try {
      await saveData("global", newGlobalData);
      console.log("New group and dashboard state saved to WordPress successfully");
    } catch (error) {
      console.error("Failed to save new group state to WordPress:", error);
    }
  };
  const REACT_APP_PARENT_ORIGIN =
    process.env.REACT_APP_PARENT_ORIGIN || "https://topteacher.com.au/";
  // Extract just the base URL without hash/path for tooltip
  const parentOriginBaseUrl = REACT_APP_PARENT_ORIGIN.split("#")[0].split("?")[0];

  const handleClick = () => {
    window.parent.postMessage(
      { type: "GO_TO_HOME" },
      "*", // or parent origin for security
    );
  };

  return (
    <WorkspaceWrap className="workspace">
      <Header>
        <Container>
          <Logo
            role="link"
            tabIndex={0}
            onClick={() => handleClick()}
            aria-label={parentOriginBaseUrl}
          >
            <PlayEdLogoSVG style={{ width: 200 }} />
          </Logo>
        </Container>
      </Header>
      <WorkspaceMain>
        <Container>
          <WorkspaceController>
            <h1>Groups</h1>
            <AddGroupBtn onClick={handleCreateNewGroup} disabled={dashboardGroups.length >= 4}>
              Add group <Add />
            </AddGroupBtn>
            <GroupWrap>
              {/* <ToolbarSegmentHeader header="Dashboard" onBack={handleBack} /> */}
              {dashboardGroups.map((group, index) => (
                <GroupItem key={group.id} groupId={group.id} groupIndex={index} />
              ))}
            </GroupWrap>
          </WorkspaceController>
        </Container>
      </WorkspaceMain>
    </WorkspaceWrap>
  );
};

const WorkspaceWrap = styled.div`
  background: rgb(249, 250, 251);
`;

const Header = styled.header`
  background: var(--widget-primary);
  padding-bottom: 150px;
`;

const Logo = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 200px;
  cursor: pointer;
  box-shadow: none;
`;

const WorkspaceMain = styled.main`
  margin-top: -150px;
  padding-bottom: 60px;
  min-height: calc(100vh - 75px);
`;

const WorkspaceController = styled.div`
  padding: 20px;
  border-radius: 8px;
  background-color: white;
  min-height: 796px;
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgb(0 0 0 / 0.1);
`;

const GroupWrap = styled(Col)`
  height: 100%;
  width: 100%;
  gap: 16px;
`;

const AddGroupBtn = styled(Button)`
  background-color: var(--widget-primary);
  color: white;
  display: flex;
  justify-content: center;
  padding: 0.5rem 0.75rem;
  margin-bottom: 16px;
  gap: 10px;
  svg {
    width: 18px;
    path {
      fill: white;
    }
  }
`;
